import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bike, Clock, User, CircleStop, Phone } from "lucide-react";
import { RentalWithRelations } from "@/types/models";
import { format, differenceInSeconds } from "date-fns";
import { useToast } from "@/components/ui/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { completeRental } from "@/integrations/supabase/services";
import GroupedRentalCompletionModal from "./GroupedRentalCompletionModal";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";

interface GroupedRentalCardProps {
  rentals: RentalWithRelations[];
}

const GroupedRentalCard = ({ rentals }: GroupedRentalCardProps) => {
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isOvertime, setIsOvertime] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  // Usar el primer alquiler como referencia para tiempos
  const primaryRental = rentals[0];

  const completeMutation = useMutation({
    mutationFn: async () => {
      // Completar todos los alquileres del grupo
      const promises = rentals.map(rental => completeRental(rental.id));
      await Promise.all(promises);
    },
    onSuccess: () => {
      toast({
        title: "¡Éxito!",
        description: `${rentals.length} alquiler(es) finalizado(s) correctamente.`,
      });
      queryClient.invalidateQueries({ queryKey: ['active-rentals'] });
      queryClient.invalidateQueries({ queryKey: ['rentals'] });
      setShowCompletionModal(false);
    },
    onError: (error) => {
      console.error("Error completing rentals:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Error al finalizar los alquileres. Intenta nuevamente.",
      });
    },
  });

  useEffect(() => {
    const calculateTimeRemaining = () => {
      if (primaryRental.expected_return_time) {
        const now = new Date();
        const returnTime = new Date(primaryRental.expected_return_time);
        const secondsDiff = differenceInSeconds(returnTime, now);
        
        setTimeRemaining(secondsDiff);
        setIsOvertime(secondsDiff < 0);
      }
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [primaryRental.expected_return_time]);

  const formatTime = (totalSeconds: number) => {
    const absSeconds = Math.abs(totalSeconds);
    const hours = Math.floor(absSeconds / 3600);
    const minutes = Math.floor((absSeconds % 3600) / 60);
    const seconds = absSeconds % 60;

    const pad = (num: number) => String(num).padStart(2, '0');

    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };

  const getTimeColor = () => {
    if (isOvertime) return "text-red-600 font-bold animate-pulse";
    if (!isOvertime && timeRemaining <= 300) return "text-red-600 font-bold animate-pulse";
    return "text-green-600 font-medium";
  };

  const getTimeLabel = () => {
    if (isOvertime) return "Tiempo excedido:";
    return "Tiempo restante:";
  };

  const startTime = primaryRental.start_date ? format(new Date(primaryRental.start_date), 'hh:mm a') : '';

  const handleCompleteRental = () => {
    setShowCompletionModal(true);
  };

  const handleConfirmCompletion = () => {
    completeMutation.mutate();
  };

  return (
    <>
      <Card className="hover:shadow-lg transition-shadow">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-bike-600" />
              <h3 className="font-semibold text-lg text-gray-800">
                {primaryRental.customer?.name}
              </h3>
              {primaryRental.customer?.phone && (
                <a
                  href={`tel:${primaryRental.customer.phone}`}
                  className="text-bike-600 hover:text-bike-700"
                >
                  <Phone className="h-4 w-4" />
                </a>
              )}
              <Badge variant="secondary" className="ml-2">
                {rentals.length}
              </Badge>
            </div>
            <div className="text-right">
              <div>{startTime}</div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Bike className="h-4 w-4 text-bike-600" />
            {isMobile ? (
              <Popover>
                <PopoverTrigger asChild>
                  <Badge variant="outline" className="bg-bike-50 text-bike-700 border-bike-200 cursor-pointer">
                    {rentals.length} Bicicletas
                  </Badge>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-2">
                  <div className="space-y-1">
                    {rentals.map(rental => (
                      <p key={rental.id} className="text-sm">
                        {rental.bike?.model} #{rental.bike?.number} - {rental.bike?.type}
                      </p>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="bg-bike-50 text-bike-700 border-bike-200">
                      {rentals.length} Bicicletas
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="space-y-1">
                      {rentals.map(rental => (
                        <p key={rental.id}>
                          {rental.bike?.model} #{rental.bike?.number} - {rental.bike?.type}
                        </p>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-600">{getTimeLabel()}</span>
            <span className={getTimeColor()}>
              {formatTime(timeRemaining)}
              {isOvertime && (
                <span>⚠️</span>
              )}
            </span><span className="text-sm font-semibold">{format(new Date(rentals[0].expected_return_time), 'hh:mm a')}</span>
          </div>

          <div className="pt-3 border-t">
            <Button 
              onClick={handleCompleteRental}
              disabled={completeMutation.isPending}
              className="w-full bg-red-600 hover:bg-red-700 text-white"
              size="sm"
            >
              <CircleStop className="mr-2 h-4 w-4" />
              Finalizar Todos los Servicios
            </Button>
          </div>
        </CardContent>
      </Card>

      <GroupedRentalCompletionModal
        rentals={rentals}
        isOpen={showCompletionModal}
        onClose={() => setShowCompletionModal(false)}
        onConfirm={handleConfirmCompletion}
        isLoading={completeMutation.isPending}
      />
    </>
  );
};

export default GroupedRentalCard;
